// Public contact page — /contact.
//
// Affiliate and partner networks check for reachable, real-business contact
// details before approving a publisher; a mailto buried in the footer reads as
// thin. This page states who the company is, how to reach it, and what each
// inbox is for.
//
// Contact is email-only by the lead's choice (no phone). The address is
// support@alieffundinghub.com — routing that inbox to the lead's Gmail is a Cloudflare
// Email Routing rule (set up separately; the domain had no MX before).
import { Link } from "react-router-dom";
import { applySeo } from "../lib/seo";

const EMAIL = "support@alieffundinghub.com";

const ROUTES = [
  {
    label: "Platform access & demos",
    body: "Want to see the Funding Hub on your own client files, or get your team set up? Say what you do and how many clients you're running.",
    to: `mailto:${EMAIL}?subject=Bag%20Manager%20access`,
    cta: "Request access",
  },
  {
    label: "Support for existing companies",
    body: "Already running on the platform and something isn't behaving? Include your company name and what you were doing when it happened.",
    to: `mailto:${EMAIL}?subject=Support%20request`,
    cta: "Get support",
  },
  {
    label: "Privacy & data requests",
    body: "Access, correction, or deletion requests. If you're a client of a company that uses the platform, contact that company first — they control your file, and we'll coordinate with them.",
    to: `mailto:${EMAIL}?subject=Privacy%20request`,
    cta: "Email privacy",
  },
  {
    label: "Partnerships & press",
    body: "Lenders, data providers, and anyone who wants to work with us on the funding side.",
    to: `mailto:${EMAIL}?subject=Partnership`,
    cta: "Start a conversation",
  },
];

export default function ContactPage() {
  applySeo({
    title: "Contact | Alief Funding Hub",
    description:
      "Contact the Alief Funding Hub team about platform access, support, privacy requests, or partnerships. Operated by Alief Financial Services.",
    path: "/contact",
  });
  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-shell landing-nav-inner">
          <Link to="/" className="landing-logo" style={{ textDecoration: "none" }}>
            ALIEF <em>Funding Hub</em>
          </Link>
          <nav style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Link className="ghost-button" to="/blog">Blog</Link>
            <Link className="primary-button" to="/login">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="landing-section">
        <div className="landing-shell" style={{ maxWidth: 820 }}>
          <h1 className="landing-h2" style={{ textAlign: "left", marginBottom: 10 }}>Contact us</h1>
          <p className="landing-sub">
            A real person reads this inbox. Tell us which of these you need and we'll come back to you.
          </p>

          <div className="landing-grid" style={{ marginBottom: 30 }}>
            {ROUTES.map((r) => (
              <article key={r.label} className="landing-card">
                <h3>{r.label}</h3>
                <p className="desc">{r.body}</p>
                <p style={{ marginTop: 12 }}>
                  <a className="ghost-button" href={r.to} style={{ textDecoration: "none" }}>{r.cta}</a>
                </p>
              </article>
            ))}
          </div>

          <div className="panel-ish landing-card">
            <h3>Company details</h3>
            <dl className="contact-dl">
              <dt>Product</dt><dd>Alief Funding Hub</dd>
              <dt>Operated by</dt><dd>Alief Financial Services</dd>
              <dt>Email</dt><dd><a href={`mailto:${EMAIL}`}>{EMAIL}</a></dd>
            </dl>
            <p className="muted small" style={{ marginTop: 14 }}>
              The Funding Hub is workflow software for funding professionals. We are not a lender,
              broker of record, or credit-repair service, and we do not make credit decisions.
              See our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-shell">
          <p className="landing-legal">
            <Link to="/">Home</Link> · <Link to="/blog">Blog</Link> · <Link to="/contact">Contact</Link> ·{" "}
            <Link to="/privacy">Privacy Policy</Link> · <Link to="/terms">Terms of Service</Link> ·{" "}
            Alief Financial Services · <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
